let generatedValidationId = 0;

export function initValidatedForm(config) {
    const form = config?.form;
    const fieldConfigs = Array.isArray(config?.fields) ? config.fields : [];

    if (!(form instanceof HTMLFormElement) || fieldConfigs.length === 0) {
        return undefined;
    }

    const fields = fieldConfigs
        .map((fieldConfig) => normalizeFieldConfig(fieldConfig))
        .filter((field) => field !== null);

    if (fields.length === 0) {
        return undefined;
    }

    let liveValidationEnabled = fields.some((field) => fieldStartsInvalid(field));
    let submitted = false;

    const validateField = (field) => {
        const customMessage = typeof field.getCustomMessage === 'function'
            ? String(field.getCustomMessage() ?? '')
            : '';

        field.input.setCustomValidity(customMessage);

        if (field.input.checkValidity()) {
            clearFieldError(field);

            return '';
        }

        const message = resolveFieldMessage(field);
        showFieldError(field, message);

        return message;
    };

    const validateForm = () => {
        let firstInvalidField = null;

        for (const field of fields) {
            const message = validateField(field);
            if (message !== '' && firstInvalidField === null) {
                firstInvalidField = field;
            }
        }

        if (firstInvalidField !== null && typeof firstInvalidField.target.focus === 'function') {
            firstInvalidField.target.focus();
        }

        return firstInvalidField === null;
    };

    const handleSubmit = (event) => {
        liveValidationEnabled = true;
        submitted = true;

        if (validateForm()) {
            return;
        }

        event.preventDefault();
    };

    const listeners = [];

    const addFieldListener = (input, type, listener) => {
        input.addEventListener(type, listener);
        listeners.push(() => input.removeEventListener(type, listener));
    };

    for (const field of fields) {
        let edited = false;
        const handleLiveValidation = (event) => {
            if (event?.type === 'input') {
                edited = true;
            }
            // Leaving a field the server refused, without touching it, keeps the server's reason: a
            // cleared password would otherwise turn `incorrect` into `required` on the first blur.
            if (!liveValidationEnabled || (event?.type === 'blur' && !edited && !submitted)) {
                return;
            }

            validateField(field);
        };

        addFieldListener(field.input, 'input', handleLiveValidation);
        addFieldListener(field.input, 'blur', handleLiveValidation);
    }

    form.addEventListener('submit', handleSubmit);

    return {
        destroy() {
            form.removeEventListener('submit', handleSubmit);
            for (const removeListener of listeners) {
                removeListener();
            }
        },
    };
}

function normalizeFieldConfig(fieldConfig) {
    const input = fieldConfig?.input;
    const errorContainer = fieldConfig?.errorContainer;

    if (!(input instanceof HTMLElement) || !(errorContainer instanceof HTMLElement)) {
        return null;
    }

    return {
        input,
        // The visible control when `input` is only the value source (a select behind a combobox):
        // it receives focus, aria-invalid and aria-describedby, while validity is read from `input`.
        target: fieldConfig.target instanceof HTMLElement ? fieldConfig.target : input,
        errorContainer,
        errorElement: fieldConfig.errorElement instanceof HTMLElement ? fieldConfig.errorElement : null,
        errorTagName: typeof fieldConfig.errorTagName === 'string' && fieldConfig.errorTagName !== ''
            ? fieldConfig.errorTagName
            : 'small',
        errorClassName: typeof fieldConfig.errorClassName === 'string' ? fieldConfig.errorClassName : '',
        messages: typeof fieldConfig.messages === 'object' && fieldConfig.messages !== null
            ? fieldConfig.messages
            : {},
        getCustomMessage: typeof fieldConfig.getCustomMessage === 'function'
            ? fieldConfig.getCustomMessage
            : null,
    };
}

function fieldStartsInvalid(field) {
    const ariaInvalid = String(field.input.getAttribute('aria-invalid') ?? '').toLowerCase();
    if (ariaInvalid === 'true') {
        return true;
    }

    const errorElement = field.errorElement;
    if (!(errorElement instanceof HTMLElement)) {
        return false;
    }

    return !errorElement.hidden && String(errorElement.textContent ?? '').trim() !== '';
}

function resolveFieldMessage(field) {
    const validity = field.input.validity;

    if (validity?.valueMissing && typeof field.messages.valueMissing === 'string') {
        return field.messages.valueMissing;
    }

    if (validity?.typeMismatch && typeof field.messages.typeMismatch === 'string') {
        return field.messages.typeMismatch;
    }

    if (validity?.tooShort && typeof field.messages.tooShort === 'string') {
        return field.messages.tooShort;
    }

    if (validity?.patternMismatch && typeof field.messages.patternMismatch === 'string') {
        return field.messages.patternMismatch;
    }

    if (validity?.customError && typeof field.input.validationMessage === 'string' && field.input.validationMessage !== '') {
        return field.input.validationMessage;
    }

    if (typeof field.input.validationMessage === 'string' && field.input.validationMessage !== '') {
        return field.input.validationMessage;
    }

    return 'Invalid value.';
}

function showFieldError(field, message) {
    const errorElement = ensureErrorElement(field);
    field.target.setAttribute('aria-invalid', 'true');

    if (!(errorElement instanceof HTMLElement)) {
        return;
    }

    ensureErrorElementId(errorElement);
    appendDescribedBy(field.target, errorElement.id);
    errorElement.hidden = false;
    errorElement.textContent = message;
}

function clearFieldError(field) {
    field.target.setAttribute('aria-invalid', 'false');

    const errorElement = ensureErrorElement(field);
    if (!(errorElement instanceof HTMLElement)) {
        return;
    }

    ensureErrorElementId(errorElement);
    appendDescribedBy(field.target, errorElement.id);
    errorElement.hidden = true;
    errorElement.textContent = '';
}

function ensureErrorElement(field) {
    if (field.errorElement instanceof HTMLElement) {
        return field.errorElement;
    }

    const documentRef = field.errorContainer.ownerDocument ?? globalThis.document;
    if (documentRef === undefined || documentRef === null || typeof documentRef.createElement !== 'function') {
        return null;
    }

    const errorElement = documentRef.createElement(field.errorTagName);
    if (!(errorElement instanceof HTMLElement)) {
        return null;
    }

    if (field.errorClassName !== '') {
        errorElement.className = field.errorClassName;
    }

    errorElement.hidden = true;
    errorElement.setAttribute('role', 'alert');
    field.errorContainer.append(errorElement);
    field.errorElement = errorElement;

    return errorElement;
}

function ensureErrorElementId(errorElement) {
    if (typeof errorElement.id === 'string' && errorElement.id !== '') {
        return errorElement.id;
    }

    generatedValidationId += 1;
    errorElement.id = `form-validation-error-${generatedValidationId}`;

    return errorElement.id;
}

function appendDescribedBy(input, errorId) {
    const currentValue = String(input.getAttribute('aria-describedby') ?? '').trim();
    if (currentValue === '') {
        input.setAttribute('aria-describedby', errorId);

        return;
    }

    const tokens = currentValue.split(/\s+/);
    if (tokens.includes(errorId)) {
        return;
    }

    input.setAttribute('aria-describedby', `${currentValue} ${errorId}`);
}
